import React from 'react';
import { useTimezone } from '../../contexts/TimezoneContext';

export interface TimezoneAwareTimestampProps {
  dateString: string | null | undefined;
  format?: 'full' | 'short' | 'datetime' | 'time' | 'custom';
  customOptions?: Intl.DateTimeFormatOptions;
  className?: string;
  fallbackText?: string;
}

/**
 * Timezone-aware timestamp component that displays dates/times in the user's selected timezone
 */
const TimezoneAwareTimestamp: React.FC<TimezoneAwareTimestampProps> = ({
  dateString,
  format = 'full',
  customOptions,
  className,
  fallbackText = 'Not specified'
}) => {
  const { timezone } = useTimezone();

  if (!dateString) {
    return <span className={className}>{fallbackText}</span>;
  }

  try {
    const date = new Date(dateString);
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return <span className={className}>Invalid date</span>;
    }

    let formatOptions: Intl.DateTimeFormatOptions;

    if (customOptions) {
      formatOptions = { ...customOptions, timeZone: timezone };
    } else {
      switch (format) {
        case 'full':
          formatOptions = {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            timeZone: timezone
          };
          break;
        case 'short':
          formatOptions = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            timeZone: timezone
          };
          break;
        case 'datetime':
          formatOptions = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZone: timezone
          };
          break;
        case 'time':
          formatOptions = {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZone: timezone
          };
          break;
        default:
          formatOptions = {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            timeZone: timezone
          };
      }
    }

    const formattedDate = new Intl.DateTimeFormat('en-US', formatOptions).format(date);

    return (
      <time 
        dateTime={date.toISOString()} 
        className={className}
        title={`${formattedDate} (${timezone})`}
      >
        {formattedDate}
      </time>
    );
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return <span className={className}>Invalid date</span>;
  }
};

export default TimezoneAwareTimestamp;
